import { useEffect, useRef } from 'react';
import { CloseIcon, LockIcon } from './Icons';

interface LegalDialogProps {
  open: boolean;
  onClose: () => void;
}

export function LegalDialog({ open, onClose }: LegalDialogProps): React.ReactElement {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="legal-dialog"
      aria-labelledby="legal-title"
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <div className="legal-dialog__sheet">
        <header className="legal-dialog__header">
          <div>
            <span className="eyebrow">
              <LockIcon /> Plain-language policy
            </span>
            <h2 id="legal-title">Terms & Privacy</h2>
            <p>Effective July 27, 2026</p>
          </div>
          <button className="icon-button" type="button" aria-label="Close terms and privacy" onClick={onClose}>
            <CloseIcon />
          </button>
        </header>

        <div className="legal-dialog__content">
          <section id="privacy" aria-labelledby="privacy-title">
            <span className="legal-dialog__number">01</span>
            <div>
              <h3 id="privacy-title">Privacy: what happens to your photo</h3>
              <p>
                The automatic analyzer is designed so the selected image is decoded and measured in your browser. The site does not submit the image, its facial landmarks, or the resulting shape classification to an analysis server. There is no account and no photo-upload endpoint.
              </p>
              <p>
                The analyzer runtime, WebAssembly files, and model are packaged with this website and requested from the same site origin. They were prepared from the MediaPipe software and model distribution during the site build; they do not need a third-party runtime connection to analyze your image.
              </p>
              <p>
                The selected photo and derived values remain in temporary page memory. They disappear when you reset the tool, refresh the page, or close the tab. A report is created only when you press the download button, and the PNG is generated locally with the browser’s Canvas feature.
              </p>
            </div>
          </section>

          <section aria-labelledby="camera-title">
            <span className="legal-dialog__number">02</span>
            <div>
              <h3 id="camera-title">Camera permission</h3>
              <p>
                Camera access is requested only after you choose “Use camera.” The preview stream stays on the device and stops when you capture, cancel, switch methods, or leave the page. You can revoke permission in the browser settings at any time.
              </p>
            </div>
          </section>

          <section aria-labelledby="network-title">
            <span className="legal-dialog__number">03</span>
            <div>
              <h3 id="network-title">Website delivery data</h3>
              <p>
                The launch version does not include advertising pixels, behavioral analytics, remote fonts, or social-media trackers. Cloudflare delivers the website and may process ordinary request information such as an IP address, browser headers, requested asset, and security events as part of its infrastructure. That request data does not include the portrait because the portrait is never requested by the website server.
              </p>
              <p>
                Browser security policy restricts runtime network connections to this site’s own origin. No security mechanism is infallible, but this policy is intended to make the privacy promise enforceable rather than merely promotional.
              </p>
            </div>
          </section>

          <section aria-labelledby="result-title">
            <span className="legal-dialog__number">04</span>
            <div>
              <h3 id="result-title">What the result means</h3>
              <p>
                Face-shape categories are simplified styling conventions. The tool compares visible two-dimensional proportions with seven geometric profiles and may return a blended or borderline result. Match bars express relative similarity inside this tool; they are not probabilities, medical measurements, biometric verification, or a scientifically validated accuracy score.
              </p>
              <p>
                The service does not attempt to determine identity, attractiveness, health, age, emotion, ethnicity, gender, or other sensitive traits. Styling suggestions are optional ideas, not professional advice or rules about appearance.
              </p>
            </div>
          </section>

          <section aria-labelledby="terms-title">
            <span className="legal-dialog__number">05</span>
            <div>
              <h3 id="terms-title">Terms of use</h3>
              <p>
                You may use this free tool for lawful personal or professional styling reference. Only analyze a photo that you own or are authorized to use. Do not use the service to identify, monitor, harass, discriminate against, or make consequential decisions about another person.
              </p>
              <p>
                The service is provided on an “as available” basis without a guarantee that every browser, image, camera, measurement, or classification will be correct. You remain responsible for decisions made from the result. To the extent allowed by law, DIFFT LLC is not liable for indirect, incidental, or consequential loss arising from use of the service.
              </p>
              <p>
                The site’s original interface, copy, illustrations, and classification presentation are protected by applicable intellectual-property law. Open-source software remains subject to its respective license terms.
              </p>
            </div>
          </section>

          <section aria-labelledby="changes-title">
            <span className="legal-dialog__number">06</span>
            <div>
              <h3 id="changes-title">Changes and contact</h3>
              <p>
                Material policy changes will be reflected here with a new effective date. This site is operated by DIFFT LLC. Privacy or security reports may be sent to the contact address published in the site’s security file.
              </p>
            </div>
          </section>
        </div>

        <footer className="legal-dialog__footer">
          <p>One policy, one page, no forced navigation away from your result.</p>
          <button className="button button--primary" type="button" onClick={onClose}>
            Close policy
          </button>
        </footer>
      </div>
    </dialog>
  );
}
